@if (label) {
  <app-header-info-title
    class="mb-1"
    [headerText]="label"
    [infoText]="labelInfo"
  ></app-header-info-title>
}

<div class="flex-1" [ngClass]="{ 'relative mr-7': fieldText && hasPencilIcon }">
  <ng-container *ngTemplateOutlet="(fieldControl && !isDisabled) ? editableField : displayedField"></ng-container>
</div>

<ng-template #displayedField>
  <ng-container *ngTemplateOutlet="rowsCount > 1 ? multiLined : singleLined"></ng-container>

  @if (isPencilIconDisplayed) {
    <svg-icon
      class="pencil-icon cursor-pointer absolute top-2 -right-7 h-5 w-5 text-navy-70 invisible"
      [src]="'edit'"
      (click)="enableEditableMode($event)">
    </svg-icon>
  }
</ng-template>

<ng-template #singleLined>
  <div
    class="displayed-text flex group p-2 rounded-md min-h-10 overflow-hidden overflow-ellipsis text-base h-9"
    [ngClass]="{
      'text-navy-70': fieldText,
      'text-navy-60': !fieldText,
      'whitespace-pre-line': displayLineBreaks,
      'cursor-pointer': isClickable,
      'hoverable bg-navy-10 hover:bg-navy-30': hoverable && !isDisabled,
      'h-9': !multilineDisplayText
    }"
    [ngbTooltip]="shouldDisplayTooltip ? fullTextTooltip : null"
    [openTooltipWhenOverflowedAndHovered]="true"
    [openOnWidthOverflow]="true"
    [placement]="'top'"
    (click)="isClickable && enableEditableMode($event)"
  >
    <ng-container *ngTemplateOutlet="displayedFieldBody"></ng-container>
  </div>
</ng-template>

<ng-template #multiLined>
  <div
    #dir="multilineText"
    class="displayed-text flex group p-2 rounded-md min-h-10 overflow-hidden overflow-ellipsis text-base"
    [ngClass]="{
      'text-navy-70': fieldText,
      'text-navy-60': !fieldText,
      'whitespace-pre-line': displayLineBreaks,
      'cursor-pointer': isClickable,
      'hoverable bg-navy-10 hover:bg-navy-30': hoverable && !isDisabled
    }"
    [multilineTextWithElipsis]="true"
    [linesCount]="rowsCount"
    [calcContainerHeight]="false"
    [textAlignment]="'left'"
    [ngbTooltip]="dir.isOverflown && shouldDisplayTooltip ? fullTextTooltip : null"
    [openTooltipWhenOverflowedAndHovered]="true"
    [openOnWidthOverflow]="true"
    [placement]="'top'"
    (click)="isClickable && enableEditableMode($event)"
  >
    <ng-container *ngTemplateOutlet="displayedFieldBody"></ng-container>
  </div>
</ng-template>

<ng-template #displayedFieldBody>
  @if (fieldText) {
    <p
      class="display-text {{ font }} max-w-full inline-block p-0 {{ textSizeClass }} {{ textColorClass }}"
      [ngClass]="{ truncate: !isFormatted && !multilineDisplayText, 'font-bold': isTextBold }"
      [ngbTooltip]="shouldDisplayTooltip ? fullTextTooltip : null"
      [placement]="'top'"
      [openTooltipWhenOverflowedAndHovered]="true"
      [openOnWidthOverflow]="true"
    >
      <ng-container *ngTemplateOutlet="textTemplate"></ng-container>
    </p>
  } @else {
    {{ placeholder }}
  }
</ng-template>

<ng-template #editableField>
  <div class="editable-field flex h-full" [ngClass]="editableFieldWrapperClasses">
    @if (!isFormatted) {
      <textarea
        #textarea
        class="p-2 outline-none text-base text-navy-90 min-h-full resize-none rounded-md border border-navy-50 bg-white focus:border-navy-90 overflow-y-hidden"
        [attr.maxLength]="maxLength"
        [attr.wrap]="isOneLine ? 'off' : 'soft'"
        [class.h-8]="rowsCount <= 1"
        [class.min-h-18]="rowsCount > 1"
        [class.flex-1]="confirmationButtonsPosition === 'right'"
        [formControl]="fieldControl"
        (keydown)="textChange($event)"
        (keydown.escape)="cancelEditing()"
        (keydown.enter)="submitOnEnter ? submitEditing($event) : preventNewLine($event)"
        (keydown.shift.enter)="preventNewLine($event)"
      ></textarea>
    }

    @if (isFormatted) {
      <app-rich-textarea
        [class.flex-1]="confirmationButtonsPosition === 'right'"
        [formControl]="fieldControl"
        [focusAfterEditorCreated]="true"
        [placeholder]="''"
        [displayCharactersCounter]="displayCharactersCounter"
        [maxLength]="maxLength"
        [enterEnabled]="!submitOnEnter"
        [preserveWhitespace]="preserveWhitespace"
        (keydown.escape)="cancelEditing()"
        (keydown.enter)="submitOnEnter && submitEditing($event)"
      ></app-rich-textarea>
    }

    <div class="field-controls self-start relative" [ngClass]="editConfirmationButtonsWrapperClasses">
      <div class="flex gap-2" [ngClass]="editConfirmationButtonsClasses">
        <app-icon-button
          class="w-8 h-8 bg-white"
          [type]="'secondary'"
          [svgIconPath]="'chip-remove'"
          [iconSize]="'basic'"
          (click)="cancelEditing(); $event.stopPropagation()"
        ></app-icon-button>

        <div
          [ngbTooltip]="
            !customValidatorCheck(fieldControl.value) && customValidatorErrorMessage
              ? (customValidatorErrorMessage | translate)
              : null
          "
        >
          <app-icon-button
            class="checkmark-button w-8 h-8"
            data-test-id="confirm-btn"
            [type]="'primary'"
            [disabled]="isSaveButtonDisabled"
            [svgIconPath]="'checkmark'"
            [iconSize]="'basic'"
            (click)="submitEditing($event); $event.stopPropagation()"
          ></app-icon-button>
        </div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #fullTextTooltip>
  <span class="w-56">
    <ng-container *ngTemplateOutlet="textTemplate"></ng-container>
  </span>
</ng-template>

<ng-template #textTemplate>
  @if (isFormatted) {
    <app-formatted-text [text]="textToDisplay"></app-formatted-text>
  } @else {
    {{ textToDisplay }}
  }
</ng-template>
